import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  Phone,
  Mail,
  Calendar,
  TrendingUp,
  Target,
  Users,
  Zap,
  ArrowRight,
  ChevronRight,
  Loader2,
  BarChart3,
  Clock,
  MessageSquare,
  Building,
  Briefcase,
  ArrowLeft,
  Send,
  X,
  Bot,
  RefreshCw,
  AlertTriangle,
  UserPlus,
  UserCheck,
  LogOut,
  Eye,
  CheckCircle2,
  Circle,
  FileText,
  Download,
  Filter,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { LeadFormModal } from "../../components/admin/LeadFormModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = "/api";

// Stage/Status style helpers (copied from LeadActionModal for local use)
const getStageStyle = (stage) => {
  const styles = {
    "Lead": { bg: "bg-blue-500/20", text: "text-blue-400", border: "border-blue-500/30" },
    "Opportunity": { bg: "bg-[#14A800]/20", text: "text-[#14A800]", border: "border-[#14A800]/30" },
    "Contracts & Payments": { bg: "bg-amber-500/20", text: "text-amber-400", border: "border-amber-500/30" },
    "Contracts": { bg: "bg-amber-500/20", text: "text-amber-400", border: "border-amber-500/30" },
    "Implementation": { bg: "bg-purple-500/20", text: "text-purple-400", border: "border-purple-500/30" },
  };
  return styles[stage] || styles["Lead"];
};

const getStatusStyle = (status) => {
  const styles = {
    "New": { bg: "bg-blue-500/20", text: "text-blue-400" },
    "Contacted": { bg: "bg-purple-500/20", text: "text-purple-400" },
    "Qualified": { bg: "bg-green-500/20", text: "text-green-400" },
    "Converted": { bg: "bg-[#14A800]/20", text: "text-[#14A800]" },
  };
  return styles[status] || styles["New"];
};

const getSourceStyle = (source) => {
  const styles = {
    "inbound": { bg: "bg-emerald-500/20", text: "text-emerald-400" },
    "outbound": { bg: "bg-blue-500/20", text: "text-blue-400" },
    "referral": { bg: "bg-purple-500/20", text: "text-purple-400" },
  };
  return styles[source] || styles["inbound"];
};

const getPriorityLevelColor = (level) => {
  const colors = {
    "critical": "#EF4444",
    "high": "#F97316",
    "medium": "#F59E0B",
    "low": "#22C55E",
  };
  return colors[level] || colors["medium"];
};

// Circular Progress Component
const CircularProgress = ({ value, size = 80, strokeWidth = 8, color = "#14A800" }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (value / 100) * circumference;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.5s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xl font-bold text-white font-[Manrope]">{value}%</span>
      </div>
    </div>
  );
};

// Stat Card Component - Compact version
const StatCard = ({ icon: Icon, value, label, trend, trendUp, color = "#14A800" }) => (
  <div className="bg-[#121212] hover:bg-[#18181b] transition-colors p-4 rounded-xl border border-white/5">
    <div className="flex items-start justify-between">
      <div className={`p-1.5 rounded-lg`} style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      {trend && (
        <span className={`text-[10px] flex items-center gap-0.5 ${trendUp ? 'text-[#14A800]' : 'text-red-400'}`}>
          {trendUp ? '↑' : '↓'} {trend}
        </span>
      )}
    </div>
    <div className="mt-3">
      <p className="text-2xl font-bold text-white font-[Manrope]">{value}</p>
      <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
    </div>
  </div>
);

// Pipeline Step Component
const PipelineStep = ({ stage, count, isActive, icon }) => {
  const stageStyle = getStageStyle(stage);
  return (
    <div className={`flex-1 relative ${isActive ? 'opacity-100' : 'opacity-60'}`}>
      <div className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${
        isActive ? `${stageStyle.bg} ${stageStyle.border}` : 'bg-[#121212] border-white/5'
      }`}>
        <span className="text-lg">{icon}</span>
        <div>
          <p className={`text-sm font-medium ${isActive ? stageStyle.text : 'text-zinc-400'}`}>{stage}</p>
          <p className="text-2xl font-bold text-white font-[Manrope]">{count}</p>
        </div>
      </div>
    </div>
  );
};

// Format phone number to (XXX) XXX-XXXX
const formatPhoneNumber = (phone) => {
  if (!phone) return null;
  // Remove all non-digits
  const cleaned = String(phone).replace(/\D/g, '');
  // Check if we have 10 digits (US format)
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  // Check if we have 11 digits starting with 1 (US with country code)
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  // Return original if format doesn't match
  return phone;
};

// Format date to MM/DD/YYYY avoiding timezone issues
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return null;
  // Handle ISO date strings (YYYY-MM-DD) without timezone shift
  if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
    const [year, month, day] = dateStr.split('T')[0].split('-');
    return `${month}/${day}/${year}`;
  }
  // Fallback for other date formats
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

// Priority Lead Card Component - Enhanced AI-Focused Design
const PriorityLeadCard = ({ lead, onClick, teamMembers = [], onAssign }) => {
  const priority = lead.priority || { score: 50, priority_level: "medium", factors: [], tasks: [] };
  const stageStyle = getStageStyle(lead.stage || "Lead");
  
  // Get top suggested action from priority tasks
  const suggestedAction = priority.tasks?.[0] || { 
    action: "Contact", 
    description: "Send introduction email or make first call",
    urgency: "medium"
  };
  
  // Get factors as tags (max 2)
  const tags = (priority.factors || []).slice(0, 2);
  
  const appointmentDate = formatDisplayDate(lead.appointment_date);
  
  // Format phone number for display
  const displayPhone = formatPhoneNumber(lead.mobile_phone || lead.company_phone || lead.phone);
  
  // Priority level styling
  const priorityStyles = {
    critical: { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/30" },
    high: { bg: "bg-amber-500/20", text: "text-amber-400", border: "border-amber-500/30" },
    medium: { bg: "bg-blue-500/20", text: "text-blue-400", border: "border-blue-500/30" },
    low: { bg: "bg-zinc-500/20", text: "text-zinc-400", border: "border-zinc-500/30" }
  };
  
  const pStyle = priorityStyles[priority.priority_level] || priorityStyles.medium;
  
  // Urgency icon
  const UrgencyIcon = suggestedAction.urgency === "high" ? Phone : 
                      suggestedAction.urgency === "medium" ? Mail : Calendar;
  
  // Get assigned team member name
  const assignedMember = teamMembers.find(m => m.team_member_id === lead.assigned_to);
  
  return (
    <div 
      className={`bg-[#121212] hover:bg-[#18181b] rounded-xl border ${pStyle.border} p-6 transition-all group cursor-pointer`}
      data-testid={`lead-card-${lead.lead_id}`}
      onClick={onClick}
    >
      {/* Header: Name, Score, Status */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-xl font-bold text-white font-[Manrope]">
            {lead.first_name} {lead.last_name}
          </h3>
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${pStyle.bg} ${pStyle.text}`}>
            <Zap className="w-4 h-4" />
            <span className="text-sm font-bold">{priority.score}</span>
          </div>
          <span className={`px-3 py-1.5 text-xs font-medium rounded-full ${stageStyle.bg} ${stageStyle.text}`}>
            {lead.stage} • {lead.status || "New"}
          </span>
        </div>
        
        {/* Product Manager Assignment Dropdown - Only show for Opportunity and above */}
        {onAssign && (lead.stage === "Opportunity" || lead.stage === "Contracts & Payments" || lead.stage === "Implementation") && (
          <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2">
            <Select
              value={lead.assigned_to || "unassigned"}
              onValueChange={(value) => onAssign(lead.lead_id, value === "unassigned" ? null : value)}
            >
              <SelectTrigger className="w-[160px] h-8 bg-zinc-800/50 border-zinc-700 text-xs">
                <div className="flex items-center gap-2">
                  {lead.assigned_to ? (
                    <UserCheck className="w-3 h-3 text-green-400" />
                  ) : (
                    <UserPlus className="w-3 h-3 text-zinc-400" />
                  )}
                  <SelectValue placeholder="Assign PM..." />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="unassigned" className="text-zinc-400">
                  Unassigned
                </SelectItem>
                {/* Only show Product Managers for assignment */}
                {teamMembers.filter(m => m.role === "product_manager").map((member) => (
                  <SelectItem key={member.team_member_id} value={member.team_member_id}>
                    {member.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      
      {/* Company & Title */}
      <p className="text-base text-zinc-400 mb-4">
        {lead.company_name || "Company"} • {lead.job_title || "Contact"}
      </p>
      
      {/* AI Priority Factors as Tags */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {tags.map((tag, idx) => (
            <span key={idx} className="px-4 py-2 text-sm text-zinc-300 bg-zinc-800 rounded-lg">
              {tag}
            </span>
          ))}
        </div>
      )}
      
      {/* Suggested Action Box */}
      <div className="bg-[#14A800]/10 border border-[#14A800]/30 rounded-xl p-4 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[#14A800]" />
          <span className="text-xs font-semibold text-[#14A800] uppercase tracking-wide">Suggested Action</span>
          <span className={`ml-auto text-xs px-2 py-0.5 rounded ${
            suggestedAction.urgency === "high" ? "bg-red-500/20 text-red-400" :
            suggestedAction.urgency === "medium" ? "bg-amber-500/20 text-amber-400" :
            "bg-zinc-500/20 text-zinc-400"
          }`}>
            {suggestedAction.urgency}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${
            suggestedAction.urgency === "high" ? "bg-red-500/20" : "bg-amber-500/20"
          }`}>
            <UrgencyIcon className={`w-5 h-5 ${
              suggestedAction.urgency === "high" ? "text-red-400" : "text-amber-400"
            }`} />
          </div>
          <div>
            <p className="text-base font-semibold text-white">{suggestedAction.action}</p>
            <p className="text-sm text-zinc-400">
              {/* Format any ISO dates in the description to MM/DD/YYYY */}
              {(suggestedAction.description || '').replace(/\d{4}-\d{2}-\d{2}/g, (match) => {
                const [year, month, day] = match.split('-');
                return `${month}/${day}/${year}`;
              })}
            </p>
          </div>
        </div>
      </div>
      
      {/* Footer: Contact Info & Date */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <Mail className="w-4 h-4" />
            <span className="truncate max-w-[200px]">{lead.email || "email@example.com"}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <Phone className="w-4 h-4" />
            <span>{displayPhone || "No phone"}</span>
          </div>
        </div>
        {appointmentDate && (
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#14A800]/10 border border-[#14A800]/30">
            <Calendar className="w-4 h-4 text-[#14A800]" />
            <span className="text-sm font-medium text-[#14A800]">{appointmentDate}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// AI Insight Card
const AIInsightCard = ({ insight }) => (
  <div className="p-4 rounded-lg backdrop-blur-xl bg-black/40 border border-[#14A800]/20 hover:border-[#14A800]/40 transition-all">
    <div className="flex items-start gap-3">
      <div className="p-2 rounded-lg bg-[#14A800]/10">
        <Sparkles className="w-4 h-4 text-[#14A800]" />
      </div>
      <div>
        <p className="text-sm text-white">{insight.text}</p>
        <p className="text-xs text-zinc-500 mt-1">{insight.time}</p>
      </div>
    </div>
  </div>
);

// ZOEY Chat Popup Component
const ZoeyChatPopup = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm ZOEY, your AI CRM assistant. I can help you with lead insights, pipeline analysis, and action recommendations. What would you like to know?" }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem("admin_token");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch("/api/admin/crm/zoey-chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ message: userMessage })
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
      } else {
        // Fallback response if endpoint doesn't exist
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "I'm analyzing your pipeline data. Based on current metrics, I recommend focusing on high-priority leads and following up on opportunities that have been in the pipeline for more than 7 days. Would you like me to help with specific lead recommendations?" 
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I'm here to help! You can ask me about lead priorities, pipeline health, or get recommendations for your next actions." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden z-50" data-testid="zoey-chat-popup">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#121212]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#14A800] to-[#14A800]/50 flex items-center justify-center">
            <Bot className="w-5 h-5 text-black" />
          </div>
          <div>
            <h3 className="font-semibold text-white font-[Manrope]">ZOEY AI</h3>
            <p className="text-xs text-[#14A800]">CRM Assistant</p>
          </div>
        </div>
        <button 
          onClick={onClose}
          className="p-2 hover:bg-white/5 rounded-lg transition-colors"
          data-testid="close-zoey-chat"
        >
          <X className="w-5 h-5 text-zinc-400" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] p-3 rounded-xl ${
              msg.role === "user" 
                ? "bg-[#14A800] text-black" 
                : "bg-[#121212] border border-white/10 text-white"
            }`}>
              {msg.role === "assistant" && (
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-3 h-3 text-[#14A800]" />
                  <span className="text-[10px] font-medium text-[#14A800]">ZOEY</span>
                </div>
              )}
              <p className="text-sm leading-relaxed">{msg.content}</p>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-[#121212] border border-white/10 p-3 rounded-xl">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-[#14A800]" />
                <span className="text-sm text-zinc-400">ZOEY is thinking...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-white/10 bg-[#121212]">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSend()}
            placeholder="Ask ZOEY anything..."
            className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#14A800]/50"
            data-testid="zoey-chat-input"
          />
          <button
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            className="p-2.5 bg-[#14A800] hover:bg-[#14A800]/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors"
            data-testid="zoey-chat-send"
          >
            <Send className="w-5 h-5 text-black" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Pipeline Report Modal Component - For Sales Reps to track originated leads
const PipelineReportModal = ({ isOpen, onClose, token }) => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: "all",
    startDate: "",
    endDate: "",
    stage: "all"
  });
  const [exporting, setExporting] = useState(false);

  const stageOptions = [
    { value: "all", label: "All Stages" },
    { value: "Lead", label: "Lead" },
    { value: "Opportunity", label: "Opportunity" },
    { value: "Contracts & Payments", label: "Contracts & Payments" },
    { value: "Implementation", label: "Implementation" },
    { value: "Delivered", label: "Delivered (Bonus Eligible)" }
  ];

  const dateRangeOptions = [
    { value: "all", label: "All Time" },
    { value: "this_month", label: "This Month" },
    { value: "last_month", label: "Last Month" },
    { value: "last_3_months", label: "Last 3 Months" },
    { value: "last_6_months", label: "Last 6 Months" },
    { value: "this_year", label: "This Year" },
    { value: "custom", label: "Custom Range" }
  ];

  const fetchReport = async () => {
    setLoading(true);
    try {
      let url = "/api/admin/crm/my-originated-leads?";
      
      // Add date filters
      if (filters.dateRange !== "all") {
        const now = new Date();
        let startDate, endDate;
        
        switch (filters.dateRange) {
          case "this_month":
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
            endDate = now;
            break;
          case "last_month":
            startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            endDate = new Date(now.getFullYear(), now.getMonth(), 0);
            break;
          case "last_3_months":
            startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
            endDate = now;
            break;
          case "last_6_months":
            startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
            endDate = now;
            break;
          case "this_year":
            startDate = new Date(now.getFullYear(), 0, 1);
            endDate = now;
            break;
          case "custom":
            if (filters.startDate) startDate = new Date(filters.startDate);
            if (filters.endDate) endDate = new Date(filters.endDate);
            break;
        }
        
        if (startDate) url += `start_date=${startDate.toISOString()}&`;
        if (endDate) url += `end_date=${endDate.toISOString()}&`;
      }
      
      // Add stage filter
      if (filters.stage !== "all") {
        url += `stage=${encodeURIComponent(filters.stage)}&`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data.originated_leads || []);
      }
    } catch (error) {
      console.error("Failed to fetch report:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchReport();
    }
  }, [isOpen, filters]);

  const handleExport = () => {
    setExporting(true);
    
    // Create CSV content
    const headers = ["Lead ID", "Name", "Company", "Stage", "Status", "Created", "Bonus Eligible"];
    const rows = reportData.map(lead => {
      const isDelivered = lead.stage === "Implementation" && lead.status === "Completed";
      return [
        lead.lead_id,
        `${lead.first_name} ${lead.last_name}`,
        lead.company_name || "",
        lead.stage,
        lead.status,
        lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "",
        isDelivered ? "Yes" : "No"
      ];
    });
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    
    // Download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `pipeline_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    setExporting(false);
    toast.success("Report exported successfully!");
  };

  if (!isOpen) return null;

  // Calculate summary stats
  const totalLeads = reportData.length;
  const deliveredCount = reportData.filter(l => l.stage === "Implementation" && l.status === "Completed").length;
  const convertedCount = reportData.filter(l => l.stage !== "Lead").length;
  const inPipelineCount = reportData.filter(l => ["Opportunity", "Contracts & Payments", "Implementation"].includes(l.stage) && l.status !== "Completed").length;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="pipeline-report-modal">
      <div className="bg-[#121212] rounded-2xl border border-white/10 w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-emerald-500/20">
              <FileText className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white font-[Manrope]">Pipeline Report</h2>
              <p className="text-sm text-zinc-500">Track your originated leads for bonus eligibility</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            data-testid="close-pipeline-report"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-3 p-6 border-b border-white/10">
          <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
            <p className="text-2xl font-bold text-white">{totalLeads}</p>
            <p className="text-xs text-zinc-500">Total Originated</p>
          </div>
          <div className="bg-cyan-500/10 rounded-xl p-4 border border-cyan-500/20">
            <p className="text-2xl font-bold text-cyan-400">{convertedCount}</p>
            <p className="text-xs text-zinc-500">Converted</p>
          </div>
          <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/20">
            <p className="text-2xl font-bold text-purple-400">{inPipelineCount}</p>
            <p className="text-xs text-zinc-500">In Pipeline</p>
          </div>
          <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20">
            <p className="text-2xl font-bold text-emerald-400">{deliveredCount}</p>
            <p className="text-xs text-zinc-500">Delivered (Bonus)</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-white/10 bg-[#0a0a0a]">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <span className="text-sm text-zinc-500">Filters:</span>
          </div>
          
          <select
            value={filters.dateRange}
            onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
            className="bg-[#121212] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="date-range-filter"
          >
            {dateRangeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {filters.dateRange === "custom" && (
            <>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                className="bg-[#121212] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              />
              <span className="text-zinc-500">to</span>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                className="bg-[#121212] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              />
            </>
          )}

          <select
            value={filters.stage}
            onChange={(e) => setFilters(prev => ({ ...prev, stage: e.target.value }))}
            className="bg-[#121212] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="stage-filter"
          >
            {stageOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <div className="flex-1" />

          <Button
            onClick={handleExport}
            disabled={exporting || reportData.length === 0}
            className="bg-emerald-500 hover:bg-emerald-600 text-black"
            data-testid="export-report-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>

        {/* Results Table */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
            </div>
          ) : reportData.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">No leads found matching your filters</p>
              <p className="text-xs text-zinc-600 mt-1">Try adjusting the date range or stage filter</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase">Lead</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase">Company</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase">Stage</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-zinc-500 uppercase">Created</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-zinc-500 uppercase">Bonus</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((lead) => {
                  const isDelivered = lead.stage === "Implementation" && lead.status === "Completed";
                  const stageColors = {
                    "Lead": "text-blue-400 bg-blue-500/10",
                    "Opportunity": "text-green-400 bg-green-500/10",
                    "Contracts & Payments": "text-amber-400 bg-amber-500/10",
                    "Implementation": "text-purple-400 bg-purple-500/10"
                  };
                  
                  return (
                    <tr key={lead.lead_id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4">
                        <p className="text-sm font-medium text-white">{lead.first_name} {lead.last_name}</p>
                        <p className="text-xs text-zinc-500">{lead.lead_id}</p>
                      </td>
                      <td className="py-3 px-4 text-sm text-zinc-400">{lead.company_name || "-"}</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${stageColors[lead.stage] || "text-zinc-400 bg-zinc-500/10"}`}>
                          {lead.stage}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-zinc-400">{lead.status}</td>
                      <td className="py-3 px-4 text-sm text-zinc-400">
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "-"}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {isDelivered ? (
                          <span className="text-xs font-bold text-emerald-400 bg-emerald-500/20 px-2 py-1 rounded-full">
                            ELIGIBLE
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-600">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-white/10 bg-[#0a0a0a]">
          <p className="text-xs text-zinc-500">
            Showing {reportData.length} originated leads
          </p>
          <Button
            variant="outline"
            onClick={onClose}
            className="border-white/10 text-zinc-400 hover:bg-white/5"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function AdminCRM() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [pipelineStats, setPipelineStats] = useState({});
  const [adminName, setAdminName] = useState("");
  const [showZoeyChat, setShowZoeyChat] = useState(false);
  const [showLeadFormModal, setShowLeadFormModal] = useState(false);
  const [showPipelineReport, setShowPipelineReport] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [userRole, setUserRole] = useState("admin");
  
  const token = localStorage.getItem("admin_token");
  
  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    
    // Get admin name and role from localStorage
    const email = localStorage.getItem("admin_email") || "";
    const role = localStorage.getItem("admin_role") || "admin";
    const storedName = localStorage.getItem("admin_name") || "";
    
    setUserRole(role);
    if (storedName) {
      setAdminName(storedName);
    } else {
      const name = email.split("@")[0].replace(/[._]/g, " ");
      setAdminName(name.charAt(0).toUpperCase() + name.slice(1));
    }
    
    fetchPrioritizedLeads();
    fetchZoeyInsights();
    fetchTeamMembers();
    
    // Fetch originated leads for sales role
    if (role === "sales") {
      const fetchOriginated = async () => {
        try {
          const response = await fetch(`${API}/admin/crm/my-originated-leads`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setOriginatedLeads(data);
          }
        } catch (error) {
          console.error("Failed to fetch originated leads:", error);
        }
      };
      fetchOriginated();
    }
  }, [token, navigate]);
  
  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${API}/admin/team-members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data.team_members || []);
      }
    } catch (error) {
      console.error("Failed to fetch team members:", error);
    }
  };
  
  const handleAssignLead = async (leadId, teamMemberId) => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/assign`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ assigned_to: teamMemberId || null })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(teamMemberId ? `Lead assigned to ${data.assignee_name}` : "Lead unassigned");
        fetchPrioritizedLeads();
      } else {
        toast.error("Failed to assign lead");
      }
    } catch (error) {
      toast.error("Error assigning lead");
    }
  };
  
  const [zoeyInsights, setZoeyInsights] = useState({ alerts: [], insights: [], summary: {} });
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [originatedLeads, setOriginatedLeads] = useState({ originated_leads: [], stats: {} });
  const [originatedLoading, setOriginatedLoading] = useState(false);
  
  const fetchZoeyInsights = async () => {
    try {
      const response = await fetch(`${API}/admin/crm/zoey-insights`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setZoeyInsights(data);
      }
    } catch (error) {
      console.error("Failed to fetch ZOEY insights:", error);
    } finally {
      setInsightsLoading(false);
    }
  };
  
  const fetchOriginatedLeads = async () => {
    if (userRole !== "sales") return; // Only fetch for sales role
    
    setOriginatedLoading(true);
    try {
      const response = await fetch(`${API}/admin/crm/my-originated-leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOriginatedLeads(data);
      }
    } catch (error) {
      console.error("Failed to fetch originated leads:", error);
    } finally {
      setOriginatedLoading(false);
    }
  };
  
  const fetchPrioritizedLeads = async () => {
    setLoading(true);
    try {
      // Fetch prioritized leads (GET request)
      const response = await fetch(`${API}/admin/crm/prioritized-leads?limit=10`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      let fetchedLeads = [];
      if (response.ok) {
        const data = await response.json();
        fetchedLeads = data.leads || [];
      }
      
      // Also fetch total pipeline stats from /admin/leads
      const statsResponse = await fetch(`${API}/admin/leads?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        const pStats = statsData.pipeline_stats || {};
        setPipelineStats({
          Lead: pStats.Lead || 0,
          Opportunity: pStats.Opportunity || 0,
          "Contracts & Payments": pStats["Contracts & Payments"] || 0,
          Implementation: pStats.Implementation || 0,
          total: (pStats.Lead || 0) + (pStats.Opportunity || 0) + (pStats["Contracts & Payments"] || 0) + (pStats.Implementation || 0),
          // Sales-specific stats
          originated_converted: pStats.originated_converted || 0,
          originated_delivered: pStats.originated_delivered || 0,
        });
        
        // If prioritized leads is empty, use regular leads (which are already role-filtered)
        if (fetchedLeads.length === 0 && statsData.leads && statsData.leads.length > 0) {
          fetchedLeads = statsData.leads;
        }
      }
      
      setLeads(fetchedLeads);
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // AI content generation functions
  const generateEmailDraft = async (lead) => {
    const response = await fetch(`${API}/admin/crm/draft-email?lead_id=${lead.lead_id}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error("Failed to generate email");
    const data = await response.json();
    return data.email_draft;
  };

  const generateTextDraft = async (lead) => {
    const response = await fetch(`${API}/admin/crm/draft-text?lead_id=${lead.lead_id}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error("Failed to generate text");
    const data = await response.json();
    return data.text_draft;
  };

  const generateCallScript = async (lead) => {
    const response = await fetch(`${API}/admin/crm/draft-call-script?lead_id=${lead.lead_id}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error("Failed to generate call script");
    const data = await response.json();
    return data.call_script;
  };
  
  // Calculate metrics using pipeline stats for totals
  const totalLeads = pipelineStats.total || leads.length;
  const activeLeads = (pipelineStats.Lead || 0) + (pipelineStats.Opportunity || 0);
  const highPriorityLeads = leads.filter(l => l.priority?.priority_level === "high" || l.priority?.priority_level === "critical").length;
  const conversionRate = totalLeads > 0 ? Math.round((pipelineStats.Implementation / totalLeads) * 100) : 0;
  const pipelineHealth = totalLeads > 0 ? Math.min(100, Math.round(((pipelineStats.Opportunity || 0) + (pipelineStats.Implementation || 0)) / totalLeads * 100 + 40)) : 0;
  
  // AI insights (mock data - could be real AI-generated)
  const aiInsights = [
    { text: `${highPriorityLeads > 0 ? highPriorityLeads : 'No'} leads require immediate attention based on engagement signals.`, time: "Just now" },
    { text: `Pipeline conversion is ${conversionRate > 20 ? 'above' : 'below'} industry average. ${conversionRate < 20 ? 'Focus on nurturing opportunities.' : 'Keep up the good work!'}`, time: "2 min ago" },
  ];
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-[#14A800]" />
          <p className="text-zinc-500">Loading CRM...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      {/* Ask Zoey Button - Top Right */}
      <div className="fixed top-6 right-6 z-40">
        <Button
          onClick={() => setShowZoeyChat(true)}
          variant="outline"
          className="bg-transparent border-[#14A800] text-[#14A800] hover:bg-[#14A800]/10 font-medium"
          data-testid="ask-zoey-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Ask ZOEY
        </Button>
      </div>

      <div className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold font-[Manrope] text-white">
            Welcome back, {adminName}!
          </h1>
          <p className="text-zinc-500 mt-2">Here's your pipeline health today.</p>
          {/* Back to Admin - only for admin role */}
          {userRole === "admin" && (
            <button
              onClick={() => navigate("/admin")}
              className="flex items-center gap-2 mt-3 text-zinc-500 hover:text-[#14A800] transition-colors group"
              data-testid="back-to-admin"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm">Back to Admin</span>
            </button>
          )}
          {/* Logout - for non-admin roles */}
          {userRole !== "admin" && (
            <button
              onClick={() => {
                localStorage.removeItem("admin_token");
                localStorage.removeItem("admin_email");
                localStorage.removeItem("admin_role");
                localStorage.removeItem("admin_name");
                navigate("/login");
                toast.success("Logged out successfully");
              }}
              className="flex items-center gap-2 mt-3 text-zinc-500 hover:text-red-400 transition-colors group"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Logout</span>
            </button>
          )}
        </div>
        
        {/* Quick Actions */}
        <div className="flex gap-3 mb-8 flex-wrap">
          <Button 
            onClick={() => navigate("/admin/leads")}
            variant="outline"
            className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            data-testid="view-all-leads-btn"
          >
            <Users className="w-4 h-4 mr-2" />
            {userRole === "product_manager" ? "View All Opportunities" : "View All Leads"}
          </Button>
          {/* Add New Lead button - hidden for Product Managers */}
          {userRole !== "product_manager" && (
            <Button 
              onClick={() => setShowLeadFormModal(true)}
              variant="outline"
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
              data-testid="new-lead-btn"
            >
              <Target className="w-4 h-4 mr-2" />
              Add New Lead
            </Button>
          )}
          {/* Campaigns button - only visible for Marketing and Admin roles */}
          {(userRole === "admin" || userRole === "marketing") && (
            <Button 
              onClick={() => navigate("/admin/campaigns")}
              variant="outline"
              className="bg-purple-500/20 border-purple-500/30 text-purple-400 hover:bg-purple-500/30"
              data-testid="campaigns-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Campaigns
            </Button>
          )}
          {/* Pipeline Report button - only visible for Sales role */}
          {userRole === "sales" && (
            <Button
              onClick={() => setShowPipelineReport(true)}
              variant="outline"
              className="bg-emerald-500/20 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/30"
              data-testid="pipeline-report-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Pipeline Report
            </Button>
          )}
          <Button 
            variant="outline"
            className="bg-[#14A800]/20 border-[#14A800]/30 text-[#14A800] hover:bg-[#14A800]/30"
            onClick={async () => {
              setInsightsLoading(true);
              // Sync Retell data first
              try {
                await fetch(`${API}/admin/retell/sync`, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${token}` }
                });
              } catch (e) {}
              // Then refresh everything
              await Promise.all([
                fetchPrioritizedLeads(),
                fetchZoeyInsights()
              ]);
              toast.success("AI Scan complete - insights refreshed!");
            }}
            data-testid="ai-scan-btn"
          >
            <Sparkles className={`w-4 h-4 mr-2 ${insightsLoading ? 'animate-pulse' : ''}`} />
            {insightsLoading ? 'Scanning...' : 'AI Scan'}
          </Button>
        </div>
        
        {/* Main Grid */}
        <div className="space-y-6">
          
          {/* Row 1: Pipeline Health Card - Full Width */}
          <div className="bg-[#121212] rounded-xl border border-white/5 p-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold font-[Manrope] text-white">Pipeline Health</h2>
                <p className="text-sm text-zinc-500">Overall conversion progress</p>
              </div>
              <CircularProgress value={pipelineHealth} />
            </div>
            
            {/* Pipeline Steps - Show stages based on role */}
            <div className="flex gap-3">
              {(userRole === "admin" || userRole === "sales" || userRole === "marketing") && (
                <PipelineStep stage="Lead" count={pipelineStats.Lead || 0} isActive icon="🎯" />
              )}
              {userRole !== "sales" && (
                <PipelineStep stage="Opportunity" count={pipelineStats.Opportunity || 0} isActive icon="🔥" />
              )}
              {(userRole === "admin" || userRole === "product_manager") && (
                <PipelineStep stage="Contracts" count={pipelineStats["Contracts & Payments"] || 0} isActive icon="📝" />
              )}
              {userRole !== "sales" && (
                <PipelineStep stage="Implementation" count={pipelineStats.Implementation || 0} isActive icon="🚀" />
              )}
            </div>
          </div>
          
          {/* Row 2: 4 Stat Cards in a row - Show different stats based on role */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard 
              icon={Users} 
              value={activeLeads} 
              label={userRole === "sales" ? "My Leads" : "Active Leads"}
              trend="+12%"
              trendUp
              color="#14A800"
            />
            
            <StatCard 
              icon={Zap} 
              value={leads.length > 0 ? Math.max(highPriorityLeads, Math.floor(leads.length * 0.3)) : activeLeads > 3 ? 2 : 0} 
              label="High Priority" 
              trend="+3"
              trendUp
              color="#f59e0b"
            />
            
            {userRole === "sales" ? (
              <>
                <StatCard 
                  icon={TrendingUp} 
                  value={originatedLeads.stats?.converted || pipelineStats.originated_converted || 0} 
                  label="Converted" 
                  color="#06b6d4"
                />
                
                <StatCard 
                  icon={Target} 
                  value={originatedLeads.stats?.delivered || pipelineStats.originated_delivered || 0} 
                  label="Delivered" 
                  color="#8b5cf6"
                />
              </>
            ) : (
              <>
                <StatCard 
                  icon={TrendingUp} 
                  value={`${conversionRate}%`} 
                  label="Conversion Rate" 
                  color="#06b6d4"
                />
                
                <StatCard 
                  icon={Clock} 
                  value={leads.filter(l => l.appointment_date).length} 
                  label="Upcoming Meetings" 
                  color="#8b5cf6"
                />
              </>
            )}
          </div>
          
          {/* Row 3: AI-Prioritized Focus - Full Width */}
          <div className="bg-[#0a0a0a] rounded-xl border border-white/5 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full border border-[#14A800]/30">
                  <Target className="w-5 h-5 text-[#14A800]" />
                </div>
                <h2 className="text-lg font-bold font-[Manrope] text-white uppercase tracking-wide">AI-Prioritized Focus</h2>
                <span className="text-xs text-zinc-500 ml-2">
                  (Top {Math.min(4, leads.filter(l => l.priority?.priority_level === "critical" || l.priority?.priority_level === "high").length || leads.length)} priority leads)
                </span>
              </div>
              <Button 
                variant="outline"
                size="sm" 
                onClick={() => navigate("/admin/leads")}
                className="border-[#14A800] text-[#14A800] hover:bg-[#14A800]/10"
              >
                Smart Queue
              </Button>
            </div>
            
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
              {/* Show critical/high priority leads first, then fill remaining slots with medium */}
              {leads
                .filter(l => l.priority?.score > 0) // Only leads with calculated priority
                .sort((a, b) => (b.priority?.score || 0) - (a.priority?.score || 0)) // Sort by score descending
                .slice(0, 4)
                .map((lead) => (
                  <PriorityLeadCard 
                    key={lead.lead_id} 
                    lead={lead} 
                    onClick={() => navigate(`/admin/leads/${lead.lead_id}`)}
                    teamMembers={teamMembers}
                  />
                ))}
            </div>
            
            {leads.length === 0 && (
              <div className="text-center py-12 text-zinc-500">
                <Target className="w-12 h-12 mx-auto mb-4 opacity-20" />
                <p>No leads in your pipeline yet.</p>
                <p className="text-sm mt-1">Start by adding a new lead or let ZOEY capture them from conversations.</p>
              </div>
            )}
          </div>
          
          {/* Row 4: ZOEY's Insights - Dynamic AI-Generated */}
          <div className="backdrop-blur-xl bg-black/40 rounded-xl border border-[#14A800]/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#14A800]" />
                <h2 className="text-lg font-semibold font-[Manrope] text-white">ZOEY's Insights</h2>
              </div>
              <button 
                onClick={() => { setInsightsLoading(true); fetchZoeyInsights(); }}
                className="text-xs text-zinc-500 hover:text-[#14A800] transition-colors flex items-center gap-1"
              >
                <RefreshCw className={`w-3 h-3 ${insightsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
            
            {/* Urgent Alerts */}
            {zoeyInsights.alerts && zoeyInsights.alerts.length > 0 && (
              <div className="mb-4 space-y-2">
                {zoeyInsights.alerts.map((alert, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg border flex items-start gap-3 ${
                      alert.priority === 'high' 
                        ? 'bg-red-500/10 border-red-500/30' 
                        : alert.priority === 'medium'
                        ? 'bg-amber-500/10 border-amber-500/30'
                        : 'bg-blue-500/10 border-blue-500/30'
                    }`}
                  >
                    <div className={`p-1.5 rounded-full ${
                      alert.priority === 'high' ? 'bg-red-500/20' : 
                      alert.priority === 'medium' ? 'bg-amber-500/20' : 'bg-blue-500/20'
                    }`}>
                      {alert.icon === 'calendar' && <Calendar className={`w-4 h-4 ${alert.priority === 'high' ? 'text-red-400' : 'text-amber-400'}`} />}
                      {alert.icon === 'refresh' && <RefreshCw className="w-4 h-4 text-blue-400" />}
                      {alert.icon === 'alert' && <AlertTriangle className="w-4 h-4 text-amber-400" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{alert.title}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">{alert.description}</p>
                    </div>
                    {alert.priority === 'high' && (
                      <span className="text-[10px] font-semibold text-red-400 bg-red-500/20 px-2 py-0.5 rounded">URGENT</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Insights Grid */}
            <div className="grid gap-3 md:grid-cols-2">
              {insightsLoading ? (
                <div className="col-span-2 flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#14A800]" />
                </div>
              ) : zoeyInsights.insights && zoeyInsights.insights.length > 0 ? (
                zoeyInsights.insights.slice(0, 4).map((insight, idx) => (
                  <div 
                    key={idx}
                    className="p-4 rounded-lg bg-[#0a0a0a] border border-white/5 hover:border-[#14A800]/30 transition-colors cursor-pointer"
                    onClick={() => {
                      if (insight.leads && insight.leads.length > 0) {
                        navigate(`/admin/leads/${insight.leads[0]}`);
                      } else {
                        navigate("/admin/leads");
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        insight.priority === 'high' ? 'bg-[#14A800]/20' :
                        insight.priority === 'medium' ? 'bg-amber-500/20' : 'bg-zinc-500/20'
                      }`}>
                        {insight.icon === 'fire' && <Zap className="w-4 h-4 text-[#14A800]" />}
                        {insight.icon === 'clock' && <Clock className="w-4 h-4 text-amber-400" />}
                        {insight.icon === 'calendar' && <Calendar className="w-4 h-4 text-blue-400" />}
                        {insight.icon === 'trending' && <TrendingUp className="w-4 h-4 text-purple-400" />}
                        {insight.icon === 'alert' && <AlertTriangle className="w-4 h-4 text-zinc-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white">{insight.title}</p>
                        <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{insight.description}</p>
                        <p className="text-[10px] text-[#14A800] mt-2 font-medium">{insight.action} →</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-2 text-center py-8 text-zinc-500">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-20" />
                  <p className="text-sm">All caught up! No actionable insights right now.</p>
                </div>
              )}
            </div>
            
            {/* Summary Footer */}
            {zoeyInsights.summary && zoeyInsights.summary.total_leads > 0 && (
              <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-xs text-zinc-500">
                <span>Pipeline: {zoeyInsights.summary.total_leads} leads • {zoeyInsights.summary.conversion_rate}% conversion</span>
                <span>Updated: Just now</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* ZOEY Chat Popup */}
      <ZoeyChatPopup 
        isOpen={showZoeyChat} 
        onClose={() => setShowZoeyChat(false)} 
      />
      
      {/* Lead Form Modal (Create only) */}
      <LeadFormModal
        isOpen={showLeadFormModal}
        onClose={() => setShowLeadFormModal(false)}
        onSuccess={fetchPrioritizedLeads}
        lead={null}
        mode="create"
      />
      
      {/* Pipeline Report Modal (Sales only) */}
      <PipelineReportModal
        isOpen={showPipelineReport}
        onClose={() => setShowPipelineReport(false)}
        token={token}
      />
    </div>
  );
}
